// Memoized markdown renderer — react-markdown + remark-gfm with custom code blocks, copy buttons, and styled elements.
import { memo, useCallback, useRef, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy } from 'lucide-react';
import { cn } from '@onestone/ui/utils';
import { writeClipboard } from '@/lib/electron';
import { lightboxStore } from '@/lib/lightboxStore';
import { FEEDBACK_DISPLAY_MS } from '@/lib/uiConstants';

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (typeof node === 'object' && node !== null && 'props' in node) {
    const el = node as React.ReactElement<{ children?: React.ReactNode }>;
    return extractText(el.props.children);
  }
  return '';
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCopy = useCallback(() => {
    writeClipboard(text)
      .then(() => {
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), FEEDBACK_DISPLAY_MS);
      })
      .catch((err: unknown) => {
        console.error('Clipboard write failed:', err);
      });
  }, [text]);

  const Icon = copied ? Check : Copy;

  return (
    <button
      onClick={handleCopy}
      className="absolute right-2 top-2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-background/50 hover:text-foreground group-hover:opacity-100 touch:opacity-100"
      aria-label="Copy code"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

interface MarkdownProps {
  content: string;
  className?: string;
}

const components: Components = {
  p: ({ node: _node, className, ...props }) => (
    <p className={cn('mb-2 leading-relaxed last:mb-0', className)} {...props} />
  ),
  h1: ({ node: _node, className, ...props }) => (
    <h1 className={cn('mb-2 mt-3 text-base font-semibold', className)} {...props} />
  ),
  h2: ({ node: _node, className, ...props }) => (
    <h2 className={cn('mb-2 mt-3 text-sm font-semibold', className)} {...props} />
  ),
  h3: ({ node: _node, className, ...props }) => (
    <h3 className={cn('mb-1 mt-2 text-sm font-semibold', className)} {...props} />
  ),
  ul: ({ node: _node, className, ...props }) => (
    <ul className={cn('mb-2 list-disc pl-5 last:mb-0', className)} {...props} />
  ),
  ol: ({ node: _node, className, ...props }) => (
    <ol className={cn('mb-2 list-decimal pl-5 last:mb-0', className)} {...props} />
  ),
  li: ({ node: _node, className, ...props }) => <li className={cn('mb-0.5', className)} {...props} />,
  a: ({ node: _node, className, ...props }) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      className={cn('text-primary underline underline-offset-2 hover:no-underline', className)}
      {...props}
    />
  ),
  blockquote: ({ node: _node, className, ...props }) => (
    <blockquote className={cn('my-2 border-l-2 border-border pl-3 text-muted-foreground', className)} {...props} />
  ),
  pre: ({ node: _node, className, children, ...props }) => {
    const text = typeof children === 'string' ? children : extractText(children);
    return (
      <div className="group relative my-2">
        <pre
          className={cn(
            'overflow-x-auto rounded-md border border-border bg-muted p-3 text-xs [&>code]:border-0 [&>code]:bg-transparent [&>code]:p-0',
            className,
          )}
          {...props}
        >
          {children}
        </pre>
        {text && <CopyButton text={text} />}
      </div>
    );
  },
  code: ({ node: _node, className, ...props }) => (
    <code
      className={cn('rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground', className)}
      {...props}
    />
  ),
  table: ({ node: _node, className, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table className={cn('w-full border-collapse text-xs [&_td]:px-2 [&_th]:px-2', className)} {...props} />
    </div>
  ),
  th: ({ node: _node, className, ...props }) => (
    <th className={cn('border-b border-border py-1 text-left font-semibold', className)} {...props} />
  ),
  td: ({ node: _node, className, ...props }) => (
    <td className={cn('border-b border-border/50 py-1', className)} {...props} />
  ),
  img: ({ node: _node, className, alt, src, ...props }) => (
    <button
      type="button"
      className="my-2 inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onClick={() => {
        const url = typeof src === 'string' ? src : '#';
        lightboxStore.open({ url, fileName: alt ?? 'image', mimeType: 'image/*', downloadUrl: url });
      }}
      title="Click to enlarge"
    >
      <img
        src={src}
        alt={alt ?? 'image'}
        loading="lazy"
        className={cn(
          'max-h-[420px] max-w-full cursor-zoom-in rounded-md border border-border bg-muted/30 shadow-sm transition-opacity hover:opacity-90',
          className,
        )}
        {...props}
      />
    </button>
  ),
};

export const Markdown = memo(function Markdown({ content, className }: MarkdownProps) {
  return (
    <div className={cn('text-sm [overflow-wrap:anywhere]', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
});
